import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * The Transform Better Playbook's own page.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/transform-better-strategies-for-successful-transformation/`),
 * sent as four screenshots on 2026-08-28 with *"referans alarak ilgili sayfayı
 * tastamam şekilde, aynısını, tıpkısını tasarla"*. Band for band that page's:
 * the two-column hero with the cover artwork beside it, the grey "Why Download
 * This Playbook?" card with the pair of cover mockups and three bolded reasons,
 * the "What You'll Gain" card with the stacked copies and three promises, the
 * "Why Choose Digitopia?" card, and the centred "Don't Miss Out!" ask. The copy
 * is that page's copy, verbatim.
 *
 * **This is not the same page as `ebook-transform-better.ts`**, and the two are
 * deliberately both here. That one is `/impact-labs/ebooks/transform-better-e-book`
 * — the 2023 e-book, five best practices down the page. This one is the
 * *Playbook*, a different document with its own PDF
 * ("Strategies for Successful Transformation"), its own address on digitopia.co
 * and its own design. Merging them would break one of two live inbound paths.
 *
 * Seventh e-book page after Transform Better, Agentic AI, Sustainability
 * Innovation, Measurement, AI in Fashion Retail and the Generative AI guide, and
 * it follows those in every place the reference and this site disagree — the
 * same three deliberate departures, for the same reasons:
 *
 *  * **The eyebrow is the site's own**, over the copy column, rather than
 *    digitopia.co's small left-pinned "STRATEGIES FOR SUCCESSFUL TRANSFORMATION"
 *    label. Every other hero here is that shape, and the words are unchanged.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean the
 *    page's only call to action silently drops every lead. The `check` below
 *    says so, as the sibling pages' do.
 *  * **A testimonial closes the page**, which the reference does not have — it
 *    is what the sibling e-book pages do, asked for on 2026-08-28 (*"en aşağıda
 *    önceden konuştuğumuz gibi testimonial template ini kullanalım"*). The card
 *    is TEMSA's: of the twenty in `content/testimonials.ts` it is the one whose
 *    words are about **structuring a transformation** and tracking it against
 *    measurable outcomes, which is this playbook's own subject. Its words are
 *    the Hall of Fame's; that file is where a change to them belongs.
 *
 * The "Why Choose Digitopia?" card has an illustration the reference's own
 * screenshot does not show (its left half is empty there, the picture having
 * failed to load in capture). Marketing sent the Digital Vitality Concept
 * diagram with the reference set, and it is the argument that paragraph is
 * making — offence and defence, six dimensions — so it sits there.
 */

/**
 * The PDF, in the `website-documents` bucket rather than in `public/`.
 *
 * It is the object `content/ebooks.ts` already lists for the `transform-better`
 * card, spelled out here because this page needs it on five buttons and that
 * file's `pdf()` helper is private to the catalogue. A page's own PDF is a local
 * constant rather than a key in `pages/links.ts`: that object is for CTAs shared
 * between pages, and two concurrent runs adding two e-books to it collide.
 */
export const PDF =
  'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + '90cdb009-4914-4c39-a2a4-27975e5397a3-Strategies%20for%20Successful%20Transformation.pdf'

export const route: RouteDef = {
  id: 'ebookTransformBetterPlaybook',
  // digitopia.co's own path, carried over verbatim so the inbound links to it
  // still land. It is the long slug rather than the catalogue's derived
  // `transform-better`, which the reference screenshots' filenames settle.
  path: 'impact-labs/ebooks/transform-better-strategies-for-successful-transformation',
  priority: 0.7,
}

export const ebookTransformBetterPlaybook: WrittenPage = {
  navLabel: 'The Transform Better Playbook',
  title: 'Transform Better: Strategies for Successful Transformation',
  description:
    'In a world where change is the only constant, staying competitive means embracing '
    + 'transformation. Download The Transform Better Playbook from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Strategies for Successful Transformation',
      headline: 'Transform Better',
      body: [
        'In a world where change is the only constant, staying competitive means embracing '
          + 'transformation. Discover how to navigate and master the complexities of business '
          + 'transformation with our essential guide, “The Transform Better Playbook: Strategies '
          + 'for Successful Transformation.”',
      ],
      ctas: [{ label: 'Download', href: PDF }],
      photo: {
        src: '/photo/transform-better-playbook-ebook-artwork.webp',
        alt:
          'The playbook’s cover artwork: rows of magenta and violet light folding away into the '
          + 'dark.',
      },
      // The artwork is square and the site's hero box is 3:2, which would take a
      // third of it off the top and bottom.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE ARTWORK IS 600–900px WIDE. Those are the largest copies of the '
        + 'cover artwork (768px), the two mockups (900px) and the Digital Vitality '
        + 'diagram (600px) anybody has sent; every other hero on the site is 2560px, '
        + 'so this one is painted at roughly its own width on a laptop with nothing '
        + 'left over for a retina screen. The diagram is the weakest of the four and '
        + 'is drawn largest relative to its size. If bigger exports exist wherever '
        + 'they were made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/transform-better-playbook-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'Why Download This Playbook?',
      points: [
        'Understand Why Transformation is Crucial: learn about the key drivers of change and why '
          + 'adapting is essential for survival and growth.',
        'Discover What Needs to Transform: explore new business models, operational structures, '
          + 'and governance frameworks that align with your strategic goals.',
        'Learn How to Transform Successfully: get practical insights on effective change '
          + 'management, leadership, and fostering a culture of innovation.',
      ],
      ctas: [{ label: 'Download the Playbook', href: PDF }],
      photo: {
        src: '/photo/transform-better-playbook-ebook-covers.webp',
        alt:
          'Two copies of The Transform Better Playbook, one behind the other, on a white '
          + 'background.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'What You’ll Gain',
      points: [
        'Actionable Strategies: clear, step-by-step guidance on implementing successful '
          + 'transformation.',
        'Real-World Case Studies: learn from the successes of industry leaders like Netflix, '
          + 'Amazon, and LEGO.',
        'Critical Success Factors: balance, measurement, and humanizing transformation for '
          + 'sustainable success.',
      ],
      ctas: [{ label: 'Download the Playbook', href: PDF }],
      photo: {
        src: '/photo/transform-better-playbook-ebook-stack.webp',
        alt: 'A stack of printed copies of The Transform Better Playbook seen from above.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Why Choose Digitopia?',
      sub:
        'At Digitopia, we’re passionate about your success. Our #transformbetter approach ensures '
        + 'that you have a trusted partner every step of the way, offering the expertise and tools '
        + 'needed for your journey.',
      ctas: [{ label: 'Download the Playbook', href: PDF }],
      photo: {
        src: '/photo/digital-vitality-concept.webp',
        alt:
          'The Digital Vitality Concept: a wheel of six dimensions (customer, operations, '
          + 'technology, governance, people and innovation), with the offence half enabling '
          + 'digital velocity and the defence half digital endurance.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Don’t Miss Out!',
      sub:
        'Don’t miss out on the opportunity to future-proof your business. Embrace change with '
        + 'confidence and turn strategic visions into tangible outcomes.',
      cta: { label: 'Download the Playbook', href: PDF },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our customers say.',
      items: [
        {
          // The Hall of Fame's TEMSA card, word for word —
          // `content/testimonials.ts` is where these words are edited, and the
          // Turkish page carries its own saying of them.
          quote:
            'Digitopia’s Digital Maturity Index has been a key element in structuring our digital '
            + 'transformation. It provided us with a clear, objective view of where we stand and '
            + 'how we compare, both within our holding and across the industry. More importantly, '
            + 'it enabled us to track progress over time and align our digital investments with '
            + 'measurable outcomes. This structured approach brings clarity, focus, and confidence '
            + 'to our transformation efforts.',
          source: 'Erdinç Uğurlu, CIO, TEMSA',
          company: 'TEMSA',
          logo: { name: 'TEMSA', src: '/logo/temsacard.png' },
          portrait: { src: '/photo/portrait-erdinc-ugurlu.png', alt: 'Erdinç Uğurlu' },
        },
      ],
    },
  ],
}
